import React, { useMemo } from 'react'
import { IconButton, Toggle, Whisper, Tooltip } from 'rsuite'
import { Cell } from 'rsuite-table'
import EyeIcon from '@rsuite/icons/legacy/Eye'
import EditIcon from '@rsuite/icons/legacy/Edit'
import TrashIcon from '@rsuite/icons/legacy/Trash'
import DownloadIcon from '@rsuite/icons/legacy/Download'
import ClockIcon from '@rsuite/icons/legacy/ClockO'
import UnlockIcon from '@rsuite/icons/legacy/Unlock'
import CalendarPlusIcon from '@rsuite/icons/legacy/CalendarPlusO'
import SignInIcon from '@rsuite/icons/legacy/SignIn'
import './actionCell.scss'

type Props = {
  rowData?: any
  dataKey: string
  onAction?: (eventKey: string | undefined, rowData: any) => void
  actionOptions?: string[] | ((rowData: any) => string[])
  disabledActions?: string[] | ((rowData: any) => string[])
  isDeleteHideOn?: string
  toggleKey?: string
}

const defaultProps = {
  rowData: undefined,
  actionOptions: [],
  onAction: () => {}
}

const ACTION_CONFIG: Record<
  string,
  { eventKey: string; icon: React.ReactElement; tooltip: string }
> = {
  View: { eventKey: '1', icon: <EyeIcon />, tooltip: 'View' },
  Edit: { eventKey: '2', icon: <EditIcon />, tooltip: 'Edit' },
  Download: { eventKey: '5', icon: <DownloadIcon />, tooltip: 'Download' },
  History: { eventKey: '6', icon: <ClockIcon />, tooltip: 'History' },
  Delete: { eventKey: '4', icon: <TrashIcon />, tooltip: 'Delete' },
  Lift: { eventKey: '7', icon: <UnlockIcon />, tooltip: 'Lift block' },
  Extend: {
    eventKey: '8',
    icon: <CalendarPlusIcon />,
    tooltip: 'Extend block'
  },
  LoginAttempts: {
    eventKey: '9',
    icon: <SignInIcon />,
    tooltip: 'Login attempts'
  }
}

const ActionCell = ({
  rowData,
  dataKey,
  actionOptions,
  disabledActions,
  isDeleteHideOn,
  onAction,
  toggleKey,
  ...props
}: Props) => {
  const resolvedActionOptions = useMemo(
    () =>
      typeof actionOptions === 'function'
        ? actionOptions(rowData)
        : actionOptions,
    [actionOptions, rowData]
  )

  const resolvedDisabledActions = useMemo(
    () =>
      typeof disabledActions === 'function'
        ? disabledActions(rowData)
        : disabledActions ?? [],
    [disabledActions, rowData]
  )

  const filteredOptions = useMemo(() => {
    const options = resolvedActionOptions?.length
      ? resolvedActionOptions.filter((opt) => opt !== 'Toggle')
      : Object.keys(ACTION_CONFIG)

    if (rowData && isDeleteHideOn && rowData[isDeleteHideOn] === false) {
      return options.filter((opt) => opt !== 'Delete')
    }

    return options
  }, [resolvedActionOptions, rowData, isDeleteHideOn])

  const showToggle = resolvedActionOptions?.includes('Toggle')

  const handleToggle = () => {
    if (typeof onAction === 'function') {
      onAction('3', rowData)
    }
  }

  return (
    <Cell {...props} className="action-cell">
      <div className="action-cell__buttons">
        {filteredOptions.map((option) => {
          const config = ACTION_CONFIG[option]
          if (!config) return null
          const isDisabled = resolvedDisabledActions.includes(option)

          return (
            <Whisper
              key={option}
              placement="bottom"
              trigger="hover"
              speaker={<Tooltip>{config.tooltip}</Tooltip>}
            >
              <IconButton
                className="action-cell__btn"
                appearance="subtle"
                size="sm"
                icon={config.icon}
                aria-label={config.tooltip}
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isDisabled) return
                  if (typeof onAction === 'function') {
                    onAction(config.eventKey, rowData)
                  }
                }}
              />
            </Whisper>
          )
        })}
        {showToggle && (
          <Toggle
            className="action-cell__toggle"
            size="md"
            checked={toggleKey ? !!rowData?.[toggleKey] : !!rowData?.isActive}
            onChange={handleToggle}
          />
        )}
      </div>
    </Cell>
  )
}

ActionCell.defaultProps = defaultProps

export default ActionCell
